import { useCallback, useEffect, useState } from 'react';
import { useSubpantalla } from '../ui/navegacionReact';
import { BarChart3, ChevronDown, Package, X } from 'lucide-react';
import { api, ErrorApi, traerTodo } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useListaUrl, consultaLista } from '../lib/urlEstado';
import ListaEstandar from '../ui/ListaEstandar';
import Paginador from '../ui/Paginador';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import ModalConfirmar from '../ui/ModalConfirmar';
import Motivo from '../experiencia/Motivo';
import Ayuda from '../ui/Ayuda';
import { textoDeAyuda } from '../ayudas/notas';
import { useDialogo } from '../lib/dialogo';
import Miniatura from '../ui/Miniatura';
import GaleriaImagenes from '../ui/GaleriaImagenes';
import { Seccion, CampoValidado, Autocompletar } from '../ui/Formulario';
import { useAvisos } from '../lib/contextoAvisos';
import { ESTADO_PRODUCTO } from '../ui/estados';
import { cantidadConUnidad } from '../experiencia/cantidades';
import { Buscador, SelectFiltro } from '../ui/Filtros';
import FichaProducto from './FichaProducto';
import BloqueAtributos from '../catalogo-vivo/BloqueAtributos';
import { faltanObligatorios, paraGuardar } from '../catalogo-vivo/fichaAtributos';

const ESTADOS = ['DISPONIBLE', 'CUARENTENA', 'RESERVADO', 'OBSOLETO'];
const TIPOS_INV = ['VENTA', 'ACTIVO'];
const UNIDADES = ['PIEZA', 'KG', 'G', 'L', 'ML', 'M', 'CM', 'CAJA', 'PAQUETE'];

// La unidad NO es catálogo por empresa, y es deliberado: volverla libre
// reintroduce pza/PZA/pieza como tres unidades, y además entra en la
// aritmética del kárdex —un ajuste de 12 no significa lo mismo en KG que
// en CAJA—. Lo que sí le faltaba es leerse en español.
const ETIQUETA_UNIDAD = {
  PIEZA: 'Pieza', KG: 'Kilogramo', G: 'Gramo', L: 'Litro', ML: 'Mililitro',
  M: 'Metro', CM: 'Centímetro', CAJA: 'Caja', PAQUETE: 'Paquete',
};

const ETIQUETA_ESTADO = {
  DISPONIBLE: 'Disponible', CUARENTENA: 'En cuarentena',
  RESERVADO: 'Reservado', OBSOLETO: 'Obsoleto',
};
const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

// `contexto` llega desde la búsqueda global: {sku} abre la ficha de ese
// producto; {proveedor} entra con la lista ya filtrada por él.
export default function Productos({ contexto, irA }) {
  const { tieneNivel } = useAuth();
  const puedeEditar = tieneNivel('ENCARGADO');

  // Los filtros y la página viven en la barra de direcciones: «productos
  // bajo mínimo de ACME» se manda por mensaje en vez de dictarse por pasos.
  const { filtros, pagina, porPagina, desplazamiento, fijarFiltro, fijarPagina } =
    useListaUrl({
      // `buscar` llega desde la búsqueda global cuando alguien pulsa «ver
      // los 347»: sin esto el enlace abriría el módulo con el filtro vacío,
      // que es peor que no ofrecerlo — parece que funcionó y no filtró nada.
      buscar: contexto?.buscar || '',
      estado: '', tipo_inventario: '', solo_activos: true,
      proveedor: contexto?.proveedor || '',
      // ANEXO-CLIENTE-01 H-7: filtrar por una etiqueta libre de la empresa.
      etiqueta: '',
    }, { porPagina: 50 });
  // Las etiquetas de la empresa, para el filtro. Vacío = no se pinta el filtro.
  const [etiquetas, setEtiquetas] = useState([]);
  useEffect(() => {
    api.get('/experiencia/etiquetas').then(setEtiquetas).catch(() => setEtiquetas([]));
  }, []);

  const [productos, setProductos] = useState([]);
  // T-20 · {sku: {completo, falta}} de lo que se ve en pantalla. Dice qué le
  // falta a un producto para venderse (precio) y facturarse (clave del SAT).
  const [completitud, setCompletitud] = useState({});
  const [total, setTotal] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [panel, setPanel] = useState(null);
  const [baja, setBaja] = useState(null);   // producto en proceso de baja
  const [ficha, setFicha] = useState(contexto?.sku || null); // SKU en ficha
  useSubpantalla('sku', !!ficha, () => setFicha(null), { valor: ficha, abrir: setFicha, enUrl: true });
  const [avisoSolicitud, setAvisoSolicitud] = useState(null);
  const [recalcular, setRecalcular] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const r = await api.getConTotal(
        `/productos?${consultaLista(filtros, { limite: porPagina, desplazamiento })}`,
      );
      setProductos(r.filas);
      setTotal(r.total);
      // Qué le falta a cada producto de esta página, por lotes. Un fallo no
      // estorba la lista: simplemente no se marca nada.
      const skus = r.filas.map((p) => p.sku);
      if (skus.length) {
        const q = skus.map((s) => `sku=${encodeURIComponent(s)}`).join('&');
        api.get(`/comercial/productos/completitud?${q}`)
          .then((c) => setCompletitud(Object.fromEntries(c.productos.map((x) => [x.sku, x]))))
          .catch(() => setCompletitud({}));
      } else {
        setCompletitud({});
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [filtros, porPagina, desplazamiento]);

  // Exportar el CATÁLOGO, no la página. Antes bajaba lo que hubiera en
  // pantalla en un archivo llamado `productos.csv`.
  const exportarTodo = useCallback(
    (alAvanzar) => traerTodo(
      (limite, desde) => `/productos?${consultaLista(filtros, {
        limite, desplazamiento: desde,
      })}`,
      { alAvanzar },
    ),
    [filtros],
  );

  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
  }, [cargar]);

  // La baja siempre pasa por el modal: el motivo es obligatorio para todos
  // (el backend lo exige) y el resultado depende del rol — un encargado la
  // aplica, un operador deja una solicitud en cola.
  async function confirmarBaja(motivo) {
    const r = await api.del(`/productos/${baja.sku}`, { motivo });
    if (r?.estado === 'PENDIENTE') setAvisoSolicitud(r.mensaje);
    cargar();
  }

  const columnas = [
    {
      etiqueta: '', alineado: 'izquierda',
      // Sin `valor`: la miniatura no se exporta a CSV, una URL firmada que
      // caduca en una hora no le sirve a nadie en una hoja de cálculo.
      render: (p) => <Miniatura src={p.imagen} nombre={p.nombre} tamano="sm" />,
      clase: 'w-12',
    },
    { etiqueta: 'SKU', valor: (p) => p.sku, clase: 'font-mono text-xs' },
    {
      etiqueta: 'Nombre',
      // `valor` queda crudo para la exportación; `render` añade, junto al
      // nombre, la marca de que a un producto de VENTA le falta algo para
      // venderse o facturarse (T-20). Un activo no se vende ni se factura,
      // así que no se marca — igual que su precio se lee «—».
      valor: (p) => p.nombre,
      render: (p) => {
        const c = completitud[p.sku];
        const incompleto = p.tipo_inventario === 'VENTA' && c && !c.completo;
        return (
          <span className="inline-flex items-center gap-2">
            <span className="font-medium text-gray-900">{p.nombre}</span>
            {incompleto && (
              <span title={`Falta: ${c.falta.map((ff) => ff.etiqueta).join(', ')}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold
                               text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                Incompleto
              </span>
            )}
          </span>
        );
      },
      clase: 'font-medium text-gray-900',
    },
    { etiqueta: 'Tipo', valor: (p) => p.tipo_inventario, clase: 'text-gray-500' },
    { etiqueta: 'Estado', valor: (p) => p.estado, render: (p) => <ChipEstado mapa={ESTADO_PRODUCTO} clave={p.estado} /> },
    {
      etiqueta: 'Existencia', alineado: 'derecha',
      // `valor` queda crudo para la exportación (una cifra por celda);
      // `render` es lo que LEE la persona, con la unidad y sin ceros de
      // relleno que se leen como miles (T-17).
      valor: (p) => `${Number(p.existencia_total ?? 0)} ${p.unidad_medida}`,
      render: (p) => cantidadConUnidad(p.existencia_total ?? 0, p.unidad_medida),
      clase: 'tabular-nums',
    },
    {
      etiqueta: 'Precio', alineado: 'derecha', tipo: 'moneda',
      valor: (p) => (p.tipo_inventario === 'VENTA' ? Number(p.precio_venta) : ''),
      render: (p) => (p.tipo_inventario === 'VENTA' ? pesos.format(p.precio_venta) : '—'),
      clase: 'tabular-nums',
    },
  ];

  if (ficha) {
    return (
      <>
        <FichaProducto
          sku={ficha}
          onVolver={() => { setFicha(null); cargar(); }}
          onEditar={puedeEditar ? (p) => setPanel({ modo: 'editar', prod: p }) : null}
          onBaja={(p) => setBaja(p)}
          onImprimirEtiqueta={irA ? (p) => irA('etiquetas', { sku: p.sku, imprimir: true }) : null}
        />
        {panel && (
          <PanelProducto
            modo={panel.modo}
            inicial={panel.prod}
            onCerrar={() => setPanel(null)}
            onGuardado={() => { setPanel(null); setFicha(null); cargar(); }}
          />
        )}
        {baja && <ModalBaja producto={baja} puedeEditar={puedeEditar} onCerrar={() => setBaja(null)} onConfirmar={confirmarBaja} />}
      </>
    );
  }

  return (
    <>
      <ListaEstandar
        titulo="Productos"
        subtitulo="El catálogo de tu inventario. Aquí das de alta, editas y das de baja."
        nota={<Ayuda id="productos.baja_resumen" />}
        filtros={
          <>
            <Buscador valor={filtros.buscar} onChange={(v) => fijarFiltro({ buscar: v })} placeholder="SKU o nombre…" />
            <SelectFiltro valor={filtros.estado} onChange={(v) => fijarFiltro({ estado: v })} vacio="Todos los estados" opciones={ESTADOS} />
            <SelectFiltro valor={filtros.tipo_inventario} onChange={(v) => fijarFiltro({ tipo_inventario: v })} vacio="Venta y activos" opciones={TIPOS_INV} />
            {etiquetas.length > 0 && (
              <SelectFiltro valor={filtros.etiqueta} onChange={(v) => fijarFiltro({ etiqueta: v })} vacio="Todas las etiquetas"
                            etiqueta="Etiqueta"
                            opciones={etiquetas.map((e) => ({ valor: String(e.id), etiqueta: `${e.nombre} (${e.productos})` }))} />
            )}
            {filtros.proveedor && (
              <button
                onClick={() => fijarFiltro({ proveedor: '' })}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)] bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-2"
              >
                Proveedor: {filtros.proveedor} <X className="h-3.5 w-3.5" />
              </button>
            )}
            <label className="text-sm text-gray-600 flex items-center gap-1.5">
              <input type="checkbox" checked={filtros.solo_activos} onChange={(e) => fijarFiltro({ solo_activos: e.target.checked })} />
              Solo activos
            </label>
          </>
        }
        accionPrincipal={puedeEditar ? { etiqueta: 'Nuevo producto', onClick: () => setPanel({ modo: 'crear', prod: {} }) } : null}
        filtrosExtra={puedeEditar ? (
          <button
            onClick={() => setRecalcular(true)}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50"
          >
            <BarChart3 className="h-4 w-4" /> Recalcular ABC
          </button>
        ) : null}
        columnas={columnas}
        filas={productos}
        cargando={cargando}
        error={error}
        onReintentar={cargar}
        exportar={{ nombre: 'productos', columnas, obtenerTodo: exportarTodo }}
        paginador={
          <Paginador
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            mostrados={productos.length}
            cargando={cargando}
            onPagina={fijarPagina}
          />
        }
        vacio={
          <EstadoVacio
            icono={Package}
            titulo="Todavía no hay productos que coincidan"
            descripcion={puedeEditar ? 'Da de alta tu primer producto con “Nuevo producto”, o ajusta los filtros.' : 'Ajusta los filtros de búsqueda.'}
          />
        }
        acciones={(p) => [
          { etiqueta: 'Ficha', onClick: () => setFicha(p.sku) },
          ...(puedeEditar ? [{ etiqueta: 'Editar', onClick: () => setPanel({ modo: 'editar', prod: p }) }] : []),
          ...(p.activo ? [{ etiqueta: puedeEditar ? 'Baja' : 'Solicitar baja', onClick: () => setBaja(p), peligro: true }] : []),
        ]}
      />

      {/* El fallo de carga lo pinta ahora la propia lista (estado de error
          con motivo en español y botón de reintentar), no una caja al pie
          que enseñaba el error crudo. */}

      {avisoSolicitud && (
        <div className="mt-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-3 flex items-start justify-between gap-3">
          <span>{avisoSolicitud}</span>
          <button onClick={() => setAvisoSolicitud(null)} className="text-blue-700 font-semibold shrink-0">Entendido</button>
        </div>
      )}

      {panel && (
        <PanelProducto
          modo={panel.modo}
          inicial={panel.prod}
          onCerrar={() => setPanel(null)}
          onGuardado={() => { setPanel(null); cargar(); }}
        />
      )}

      {baja && <ModalBaja producto={baja} puedeEditar={puedeEditar} onCerrar={() => setBaja(null)} onConfirmar={confirmarBaja} />}

      {recalcular && (
        <ModalConfirmar
          titulo="Recalcular la clasificación ABC"
          descripcion="Reordena el catálogo por el valor que movió cada producto en los últimos 90 días: A es el 20% que más mueve, B el 30% siguiente y C el resto. Es lo que decide qué conviene contar más seguido."
          aviso="Corre solo de madrugada. Este botón existe para no esperar al día siguiente después de costear un lote o cerrar una temporada."
          etiquetaConfirmar="Recalcular ahora"
          onConfirmar={async () => {
            const r = await api.post('/productos/recalcular-abc', {});
            setAvisoSolicitud(r.mensaje);
            setRecalcular(false);
            cargar();
          }}
          onCerrar={() => setRecalcular(false)}
        />
      )}
    </>
  );
}

// Dar de baja no borra, pero sí saca la mercancía del listado: por eso el
// motivo es obligatorio y el modal enseña qué va a pasar exactamente.
function ModalBaja({ producto, puedeEditar, onCerrar, onConfirmar }) {
  return (
    <ModalConfirmar
      peligro
      titulo={puedeEditar ? `Dar de baja ${producto.sku}` : `Solicitar la baja de ${producto.sku}`}
      descripcion={
        puedeEditar
          ? textoDeAyuda('productos.dar_de_baja')
          : 'Como operador no puedes dar de baja directamente. Se creará una solicitud para que un encargado la apruebe.'
      }
      cambios={[{ campo: 'Estado', antes: producto.estado, despues: 'OBSOLETO' }]}
      aviso={
        Number(producto.existencia_total ?? 0) > 0
          ? `Este producto todavía tiene ${cantidadConUnidad(producto.existencia_total, producto.unidad_medida)} en almacén. Registra su salida o merma antes de darlo de baja.`
          : null
      }
      pedirMotivo
      etiquetaConfirmar={puedeEditar ? 'Dar de baja' : 'Enviar solicitud'}
      onConfirmar={onConfirmar}
      onCerrar={onCerrar}
    />
  );
}

function PanelProducto({ modo, inicial, onCerrar, onGuardado }) {
  // Tras crear, el panel NO se cierra: pasa a modo "ya existe" para poder
  // subirle las fotos en el mismo gesto. Obligar a guardar, buscar el
  // producto en la lista y volver a abrirlo para ponerle una imagen es
  // justamente el tipo de fricción que este bloque existe para quitar.
  const [creado, setCreado] = useState(null);
  const crear = modo === 'crear' && !creado;
  const skuVigente = creado || inicial.sku;
  const avisos = useAvisos();

  const [f, setF] = useState({
    sku: inicial.sku || '', nombre: inicial.nombre || '', descripcion: inicial.descripcion || '',
    tipo_inventario: inicial.tipo_inventario || 'VENTA', unidad_medida: inicial.unidad_medida || 'PIEZA',
    estado: inicial.estado || 'DISPONIBLE',
    precio_venta: inicial.precio_venta ?? 0, stock_minimo: inicial.stock_minimo ?? 0,
    punto_reorden: inicial.punto_reorden ?? 0,
    tipo_flujo: inicial.tipo_flujo || '', estacionalidad: inicial.estacionalidad || '',
    sku_familia: inicial.sku_familia || '',
    politica_salida: inicial.politica_salida || 'NINGUNA',
    controla_lote: inicial.controla_lote ?? false,
    controla_caducidad: inicial.controla_caducidad ?? false,
    trato_iva: inicial.trato_iva || 'GENERAL',
    clave_prod_serv: inicial.clave_prod_serv || '',
  });
  const [categoria, setCategoria] = useState(
    inicial.id_categoria ? { id: inicial.id_categoria, nombre: inicial.categoria } : null,
  );
  const [categorias, setCategorias] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // ANEXO-CLIENTE-01 H-7 (20 sep 2026): las etiquetas libres de la empresa.
  // Lo de nicho —estacionalidad, tipo de flujo— pasa a ser una etiqueta que
  // el administrador crea; las dos columnas viejas siguen existiendo y solo
  // se enseñan si este producto ya las traía llenas.
  const { tieneNivel } = useAuth();
  const [etiquetas, setEtiquetas] = useState([]);
  const [misEtiquetas, setMisEtiquetas] = useState([]);       // ids elegidos
  const [etiquetasInicial, setEtiquetasInicial] = useState([]);
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');
  const [avisoEtiqueta, setAvisoEtiqueta] = useState(null);
  // «Más detalles» arranca plegado. Al editar se abre solo si el producto
  // ya trae algo ahí, para no esconder un dato que existe.
  const tieneDetalles = Boolean(inicial.descripcion || inicial.sku_familia || inicial.clave_prod_serv
    || inicial.tipo_flujo || inicial.estacionalidad || Number(inicial.punto_reorden) > 0
    || (inicial.estado && inicial.estado !== 'DISPONIBLE') || inicial.controla_lote
    || (inicial.trato_iva && inicial.trato_iva !== 'GENERAL'));
  const [masDetalles, setMasDetalles] = useState(tieneDetalles);
  // CATÁLOGO VIVO F2: los datos que pide la categoría (autor, valor en Ω…).
  // La memoria guarda lo tecleado por clave: cambiar de categoría no lo pierde.
  const [memoriaAtributos, setMemoriaAtributos] = useState({});
  const [plantillaAtributos, setPlantillaAtributos] = useState([]);
  const [guardadosAtributos, setGuardadosAtributos] = useState([]);
  const faltanAtributos = categoria?.id ? faltanObligatorios(plantillaAtributos, memoriaAtributos) : [];
  // Mientras se guarda no se abandona: cerrar a media escritura deja al
  // usuario sin saber si el producto quedo creado.
  const { idTitulo, propsPanel } = useDialogo(onCerrar, { bloqueado: guardando });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const activo = f.tipo_inventario === 'ACTIVO';

  useEffect(() => {
    api.get('/categorias').then(setCategorias).catch(() => setCategorias([]));
    api.get('/experiencia/etiquetas').then(setEtiquetas).catch(() => setEtiquetas([]));
  }, []);
  useEffect(() => {
    if (!skuVigente) return undefined;
    let vigente = true;
    api.get(`/experiencia/productos/${encodeURIComponent(skuVigente)}/etiquetas`)
      .then((l) => { if (!vigente) return; const ids = l.map((e) => e.id); setMisEtiquetas(ids); setEtiquetasInicial(ids); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [skuVigente]);

  useEffect(() => {
    if (!inicial.id) return undefined;
    let vigente = true;
    api.get(`/catalogo-vivo/catalogo/productos/${inicial.id}/atributos`)
      .then((r) => { if (vigente) setGuardadosAtributos(r.atributos || []); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [inicial.id]);
  async function guardarAtributos(id) {
    if (!categoria?.id || !id) return;
    const valores = paraGuardar(plantillaAtributos, memoriaAtributos);
    if (!Object.keys(valores).length) return;
    const r = await api.put(`/catalogo-vivo/catalogo/productos/${id}/atributos`, { valores });
    setGuardadosAtributos(r.atributos || []);
    setMemoriaAtributos({});
  }

  const mismasEtiquetas = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
  async function guardarEtiquetas(sku) {
    if (mismasEtiquetas(misEtiquetas, etiquetasInicial)) return;
    await api.put(`/experiencia/productos/${encodeURIComponent(sku)}/etiquetas`, { ids: misEtiquetas });
    setEtiquetasInicial(misEtiquetas);
  }
  async function crearEtiqueta() {
    const nombre = nuevaEtiqueta.trim();
    if (nombre.length < 1) return;
    setAvisoEtiqueta(null);
    try {
      const e = await api.post('/experiencia/etiquetas', { nombre });
      setEtiquetas((l) => [...l.filter((x) => x.id !== e.id), e].sort((x, y) => x.nombre.localeCompare(y.nombre, 'es')));
      setMisEtiquetas((ids) => (ids.includes(e.id) ? ids : [...ids, e.id]));
      setNuevaEtiqueta('');
    } catch (err) {
      setAvisoEtiqueta(err instanceof ErrorApi ? err.message : 'No se pudo crear la etiqueta.');
    }
  }

  // Validación en línea y en español. Devuelve el mensaje o null.
  const vSku = (v) => {
    if (!v?.trim()) return 'El SKU es obligatorio.';
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{1,59}$/.test(v.trim())) {
      return 'Solo letras, números, punto, guion y guion bajo. Debe empezar con letra o número.';
    }
    return null;
  };
  const vNombre = (v) => (v?.trim().length >= 2 ? null : 'Escribe un nombre de al menos 2 letras.');
  const vNoNegativo = (v) => (Number(v) >= 0 ? null : 'No puede ser negativo.');

  const listo = !vSku(crear ? f.sku : 'X-X') && !vNombre(f.nombre) && faltanAtributos.length === 0;

  async function guardar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    const comun = {
      nombre: f.nombre.trim(),
      descripcion: f.descripcion || null,
      estado: f.estado,
      // El catálogo manda: si se eligió una existente va su id; si se
      // escribió una nueva va el texto y el servidor la resuelve.
      ...(categoria?.id ? { id_categoria: categoria.id }
        : { categoria: categoria?.nombre || null }),
      precio_venta: Number(f.precio_venta) || 0,
      stock_minimo: Number(f.stock_minimo) || 0,
      punto_reorden: Number(f.punto_reorden) || 0,
      tipo_flujo: f.tipo_flujo || null,
      estacionalidad: f.estacionalidad || null,
      sku_familia: f.sku_familia || null,
      politica_salida: f.politica_salida,
      // Lo fiscal. La clave vacía NO es un olvido: significa «la que
      // tenga su categoría», que es como se resuelve el catálogo entero.
      trato_iva: f.trato_iva,
      clave_prod_serv: f.clave_prod_serv.trim() || null,
    };
    try {
      if (crear) {
        const nuevo = await api.post('/productos', {
          sku: f.sku.trim().toUpperCase(),
          tipo_inventario: f.tipo_inventario,
          unidad_medida: f.unidad_medida,
          controla_lote: f.controla_lote,
          controla_caducidad: f.controla_caducidad,
          ...comun,
        });
        await guardarEtiquetas(nuevo.sku);
        await guardarAtributos(nuevo.id);
        setCreado(nuevo.sku);
        avisos.exito(`${nuevo.sku} quedó creado.`);
      } else {
        await api.patch(`/productos/${skuVigente}`, comun);
        await guardarEtiquetas(skuVigente);
        await guardarAtributos(inicial.id);
        avisos.exito('Cambios guardados.');
        onGuardado();
      }
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onCerrar} />
      {/* Modal ANCHO y no un cajón de 448 px: once campos en una columna
          estrecha son intolerables para quien da de alta 300 SKUs. Por esa
          maquetación propia —cuerpo con desplazamiento y pie fijo— usa el
          hook y no el componente `Modal`, pero el teclado se comporta igual. */}
      <div {...propsPanel}
           className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl
                      max-h-full flex flex-col focus:outline-none">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 id={idTitulo} className="font-semibold text-gray-900">
            {crear ? 'Nuevo producto' : `Editar ${skuVigente}`}
          </h2>
          <button type="button"
                  onClick={() => (creado ? onGuardado() : onCerrar())}
                  aria-label={crear ? 'Cerrar nuevo producto' : `Cerrar edición de ${skuVigente}`}
                  className="text-gray-500 hover:text-gray-900 rounded
                             focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-1">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={guardar} className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {creado && (
            <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-2.5">
              <strong>{creado}</strong> quedó creado. Ya puedes ponerle sus fotos aquí abajo.
            </div>
          )}

          {/* ANEXO-CLIENTE-01 H-7 (20 sep 2026). Arriba, lo obligatorio y lo
              que usa todo el mundo; lo demás, detrás de «Más detalles»,
              plegado. Los textos de ayuda no cambian: se movieron. */}
          <Seccion titulo="Lo básico"
                   descripcion="Con esto ya se puede guardar. Lo demás trae valores razonables y se puede cambiar después.">
            {crear && (
              <CampoValidado etiqueta="SKU" requerido valor={f.sku} validar={vSku}
                             ayuda="Va impreso en la etiqueta. No se puede cambiar después.">
                {(p) => <input {...p} value={f.sku} onChange={set('sku')}
                               autoCapitalize="characters" autoFocus />}
              </CampoValidado>
            )}
            <CampoValidado etiqueta="Nombre" requerido valor={f.nombre} validar={vNombre}
                           ancho={crear ? undefined : 'completo'}>
              {(p) => <input {...p} value={f.nombre} onChange={set('nombre')} />}
            </CampoValidado>

            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Categoría</span>
              <Autocompletar opciones={categorias} valor={categoria} onChange={setCategoria}
                             placeholder="Frenos, Aceites…" />
              <span className="block text-xs text-gray-500 mt-1">
                Si escribes una que no existe, se crea. Las que solo cambian en
                mayúsculas o espacios se unifican solas.
              </span>
            </label>
            {categoria?.id && (
              <div className="sm:col-span-2">
                <BloqueAtributos idCategoria={categoria.id} memoria={memoriaAtributos}
                  onCambio={setMemoriaAtributos} atributosGuardados={guardadosAtributos}
                  onPlantilla={setPlantillaAtributos} />
              </div>
            )}

            {crear && (
              <CampoValidado etiqueta="Tipo de inventario" valor={f.tipo_inventario}
                             ayuda="Mercancía que se vende, o equipo que la empresa usa.">
                {(p) => (
                  <select {...p} value={f.tipo_inventario} onChange={set('tipo_inventario')}>
                    <option value="VENTA">Mercancía de venta</option>
                    <option value="ACTIVO">Activo de la empresa</option>
                  </select>
                )}
              </CampoValidado>
            )}
            {/* La unidad va arriba aunque casi siempre sea «Pieza»: es lo único
                del formulario que NO se puede cambiar después. */}
            {crear && (
              <CampoValidado etiqueta="Unidad de medida" valor={f.unidad_medida}
                             ayuda="No se puede cambiar después: entra en la aritmética del kárdex.">
                {(p) => (
                  <select {...p} value={f.unidad_medida} onChange={set('unidad_medida')}
                          disabled={activo}>
                    {UNIDADES.map((u) => (
                      <option key={u} value={u}>{ETIQUETA_UNIDAD[u] || u}</option>
                    ))}
                  </select>
                )}
              </CampoValidado>
            )}

            {activo ? (
              // Los campos de venta desaparecen DICIENDO POR QUÉ. Antes se
              // esfumaban en silencio y parecía un fallo de la pantalla.
              <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-700 leading-relaxed sm:col-span-2">
                <strong>Un activo no se vende por cantidad.</strong> Se controla pieza
                por pieza, con número de serie, y se entrega a una persona que responde
                por él. Aquí no hay precio, ni stock mínimo, ni punto de
                reorden: se administra desde el módulo de Activos.
              </div>
            ) : (
              <>
                <CampoValidado etiqueta="Precio de venta" valor={f.precio_venta}
                               validar={vNoNegativo}
                               ayuda="Con IVA incluido, si tu empresa lo tiene así configurado.">
                  {(p) => <input {...p} type="number" step="any" value={f.precio_venta}
                                 onChange={set('precio_venta')} />}
                </CampoValidado>
                <CampoValidado etiqueta="Stock mínimo" valor={f.stock_minimo}
                               validar={vNoNegativo}
                               ayuda="Cuando queden menos piezas que esto, TEGO te avisará para recomprar.">
                  {(p) => <input {...p} type="number" step="any" value={f.stock_minimo}
                                 onChange={set('stock_minimo')} />}
                </CampoValidado>
              </>
            )}
          </Seccion>

          <div className="border border-gray-200 rounded-xl">
            <button type="button" onClick={() => setMasDetalles((v) => !v)}
                    aria-expanded={masDetalles} aria-controls="producto-mas-detalles"
                    className="w-full flex items-center justify-between px-4 py-3 text-left">
              <span>
                <span className="block text-sm font-semibold text-gray-900">Más detalles</span>
                <span className="block text-xs text-gray-500">Descripción, impuestos, lotes, etiquetas y reposición. Nada de esto es obligatorio.</span>
              </span>
              <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${masDetalles ? '' : '-rotate-90'}`} aria-hidden="true" />
            </button>
            {masDetalles && (
              <div id="producto-mas-detalles" className="px-4 pb-4 space-y-6">
                <Seccion titulo="Cómo se describe y se agrupa"
                         descripcion="Sirve para encontrar y comparar. Nada de esto cambia cómo se mueve la mercancía.">
                  <CampoValidado etiqueta="Descripción" ancho="completo" valor={f.descripcion}>
                    {(p) => <textarea {...p} rows={2} value={f.descripcion}
                                      onChange={set('descripcion')} />}
                  </CampoValidado>
                  <CampoValidado etiqueta="SKU de familia" valor={f.sku_familia}
                                 ayuda="Agrupa variantes del mismo artículo (tallas, colores) bajo un código común.">
                    {(p) => <input {...p} value={f.sku_familia} onChange={set('sku_familia')} />}
                  </CampoValidado>
                  <CampoValidado etiqueta="Estado" valor={f.estado}
                                 ayuda="Cuarentena y reservado retienen la mercancía sin venderla.">
                    {(p) => (
                      <select {...p} value={f.estado} onChange={set('estado')}>
                        {ESTADOS.map((e) => (
                          <option key={e} value={e}>{ETIQUETA_ESTADO[e] || e}</option>
                        ))}
                      </select>
                    )}
                  </CampoValidado>
                  {/* Lo de nicho vive como etiqueta, no como campo fijo. */}
                  <div className="sm:col-span-2">
                    <span className="block text-sm font-semibold text-gray-700 mb-1">Etiquetas</span>
                    <span className="block text-xs text-gray-500 mb-2">
                      Lo que solo usa tu empresa —temporada, rotación, línea— va aquí, y se puede filtrar en la lista.
                      {tieneNivel('ADMIN') ? ' Tú puedes crear las que hagan falta.' : ' Las crea quien administra la empresa.'}
                    </span>
                    {etiquetas.length === 0 && !tieneNivel('ADMIN') && (
                      <p className="text-xs text-gray-500">Sin etiquetas en tu empresa.</p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {etiquetas.map((e) => {
                        const puesta = misEtiquetas.includes(e.id);
                        return (
                          <label key={e.id} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${
                            puesta ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)] text-[var(--color-marca)]' : 'border-gray-300 text-gray-700'}`}>
                            <input type="checkbox" className="sr-only" checked={puesta}
                                   onChange={() => setMisEtiquetas((ids) => (puesta ? ids.filter((x) => x !== e.id) : [...ids, e.id]))} />
                            {e.nombre}
                          </label>
                        );
                      })}
                    </div>
                    {tieneNivel('ADMIN') && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input value={nuevaEtiqueta} onChange={(e) => setNuevaEtiqueta(e.target.value)}
                               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearEtiqueta(); } }}
                               placeholder="Nueva etiqueta: Navidad, Alta rotación…" maxLength={40}
                               aria-label="Nueva etiqueta"
                               className="entrada max-w-xs text-sm" />
                        <button type="button" onClick={crearEtiqueta} disabled={!nuevaEtiqueta.trim()}
                                className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                          Crear etiqueta
                        </button>
                        {avisoEtiqueta && <span role="alert" className="text-xs text-red-700">{avisoEtiqueta}</span>}
                      </div>
                    )}
                  </div>
                  {/* Las dos columnas de antes solo se enseñan si este producto ya las traía. */}
                  {inicial.tipo_flujo && (
                    <CampoValidado etiqueta="Tipo de flujo" valor={f.tipo_flujo}
                                   ayuda="Cómo rota: alto, medio o lento. Es una etiqueta tuya, no una regla del sistema.">
                      {(p) => <input {...p} value={f.tipo_flujo} onChange={set('tipo_flujo')} />}
                    </CampoValidado>
                  )}
                  {inicial.estacionalidad && (
                    <CampoValidado etiqueta="Estacionalidad" valor={f.estacionalidad}
                                   ayuda="Si se vende sobre todo en una época: navidad, lluvias, regreso a clases.">
                      {(p) => <input {...p} value={f.estacionalidad} onChange={set('estacionalidad')} />}
                    </CampoValidado>
                  )}
                </Seccion>

                <Seccion titulo="Impuestos"
                         descripcion="Solo si este producto es la excepción de su categoría.">
                  <CampoValidado
                    etiqueta="Cómo se grava" valor={f.trato_iva}
                    ayuda="Lo habitual es IVA normal. Tasa 0 y exento NO son lo mismo: confundirlos se ve en la declaración.">
                    {(p) => (
                      <select {...p} value={f.trato_iva} onChange={set('trato_iva')}>
                        <option value="GENERAL">IVA normal</option>
                        <option value="TASA_0">Tasa 0 % (alimentos, medicinas)</option>
                        <option value="EXENTO">Exento de IVA</option>
                      </select>
                    )}
                  </CampoValidado>
                  <CampoValidado
                    etiqueta="Clave del SAT" valor={f.clave_prod_serv}
                    error={f.clave_prod_serv && !/^\d{8}$/.test(f.clave_prod_serv.trim())
                      ? 'Son ocho dígitos, por ejemplo 25170000.' : null}
                    ayuda="Déjala vacía y se hereda de su categoría, que es lo normal. Solo se pone aquí si este producto es la excepción.">
                    {(p) => <input {...p} inputMode="numeric" maxLength={8}
                                   value={f.clave_prod_serv} onChange={set('clave_prod_serv')}
                                   placeholder="Se hereda de la categoría" />}
                  </CampoValidado>
                </Seccion>

                {!activo && (
                  <Seccion titulo="Reposición"
                           descripcion="Cuándo hay que volver a pedir.">
                    <CampoValidado etiqueta="Punto de reorden" valor={f.punto_reorden}
                                   validar={vNoNegativo}
                                   ayuda="Al llegar aquí, el producto aparece en «qué comprar». Déjalo en 0 para no avisar.">
                      {(p) => <input {...p} type="number" step="any" value={f.punto_reorden}
                                     onChange={set('punto_reorden')} />}
                    </CampoValidado>
                  </Seccion>
                )}

                <Seccion titulo="Lotes"
                         descripcion="Cómo sale del almacén cuando se maneja por lotes.">
                  {crear && !activo && (
                    <>
                      <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
                        <input type="checkbox" checked={f.controla_lote} className="mt-0.5"
                               onChange={(e) => setF((s) => ({ ...s, controla_lote: e.target.checked }))} />
                        <span>
                          <strong>Se maneja por lotes.</strong> Cada movimiento tendrá que
                          indicar de qué lote sale. No se puede cambiar después.
                        </span>
                      </label>
                      {f.controla_lote && (
                        <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
                          <input type="checkbox" checked={f.controla_caducidad} className="mt-0.5"
                                 onChange={(e) => setF((s) => ({
                                   ...s, controla_caducidad: e.target.checked,
                                 }))} />
                          <span><strong>Los lotes caducan.</strong> Habilita el aviso de
                            caducidad y la política FEFO.</span>
                        </label>
                      )}
                    </>
                  )}
                  {!crear && !inicial.controla_lote && (
                    <p className="text-xs text-gray-500 sm:col-span-2">Este producto no se maneja por lotes. Eso se decide al crearlo y no se puede cambiar después.</p>
                  )}

                  {(f.controla_lote || inicial.controla_lote) && (
                    <CampoValidado etiqueta="Qué lote sale primero" valor={f.politica_salida}
                                   ayuda="FEFO ordena por caducidad; FIFO por antigüedad.">
                      {(p) => (
                        <select {...p} value={f.politica_salida} onChange={set('politica_salida')}>
                          <option value="NINGUNA">Lo elige quien opera</option>
                          <option value="FEFO">FEFO — lo que caduca antes</option>
                          <option value="FIFO">FIFO — lo que entró antes</option>
                        </select>
                      )}
                    </CampoValidado>
                  )}
                </Seccion>
              </div>
            )}
          </div>

          {!crear && (
            <Seccion titulo="Imágenes" columnas={1}
                     descripcion="Cada foto se sube al momento: no dependen de pulsar «Guardar».">
              <GaleriaImagenes sku={skuVigente} />
            </Seccion>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
              {error}
            </div>
          )}
        </form>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button type="button" onClick={() => (creado ? onGuardado() : onCerrar())}
                  className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300
                             bg-white text-gray-700 hover:bg-gray-50">
            {creado ? 'Listo' : 'Cancelar'}
          </button>
          <button onClick={guardar} disabled={guardando || !listo}
                  aria-describedby={!listo ? 'producto-falta' : undefined}
                  className="bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-40 text-white
                             text-sm font-semibold px-4 py-2 rounded-lg">
            {guardando ? 'Guardando…' : crear ? 'Crear producto' : 'Guardar cambios'}
          </button>
        </div>
        {/* Por qué está gris, a la vista (Doctrina 2, regla 8). */}
        <Motivo id="producto-falta" cuando={!listo && !guardando} className="px-5 pb-3 -mt-1 text-right">
          Falta {[crear && vSku(f.sku) && 'el SKU', vNombre(f.nombre) && 'el nombre',
            ...faltanAtributos.map((n) => `«${n}»`)].filter(Boolean).join(' y ')} para poder guardar.
        </Motivo>
      </div>
    </div>
  );
}

